import { FC } from 'react';
import styled from 'styled-components';
import useTheme from '../../utils/useTheme';
import { Icon } from '../Icon/Icon';
import { LabelTooltip } from '../Label/LabelTooltip';
import HealthMetric from '../HealthMetric/HealthMetric';
import { Avatar } from '../Avatar/Avatar';
import { Paragraph } from '../Paragraph/Paragraph';
import { ContentPlaceholder } from '../ContentPlaceholder/ContentPlaceholder';
import { Position, Tooltip } from '../Tooltip/Tooltip';

const StatElement = styled.div`
  display: inline-flex;
  flex-direction: row;
  flex: 0 0 auto;
  justify-content: center;
  min-width: 100px;
  overflow-x: hidden;
  padding: 20px;
  width: 100%;
`;

const StatWrapper = styled.div<Pick<IStatProps, 'variation'>>`
  align-items: center;
  display: ${({ variation }) => (variation === 'panel' ? 'flex' : 'block')};
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const StatTitle = styled.div<Pick<IStatProps, 'titlePosition' | 'variation'>>`
  padding-bottom: ${({ titlePosition }) => (titlePosition === 'bottom' ? '5px' : null)};
  text-align: ${({ variation }) => (variation === 'panel' ? 'center' : null)};
`;

const StatTooltip = styled.div`
  display: inline-block;
  margin-left: 5px;
`;

const StatValueWrapper = styled.div<Pick<IStatProps, 'variation' | 'titlePosition'>>`
  display: ${({ variation }) => (variation === 'inline' ? 'flex' : null)};
  margin-bottom: ${({ titlePosition, variation }) =>
    variation === 'inline' ? '10px' : titlePosition === 'bottom' ? '0' : '5px'};
  margin-top: ${({ variation }) => (variation === 'inline' ? '10px' : null)};
`;

const StatValue = styled.div<Pick<IStatProps, 'variation'>>`
  display: flex;
  flex-direction: column;
  align-items: ${({ variation }) => (variation === 'panel' ? 'center' : null)};
`;

const StatComparison = styled.div<Pick<IStatProps, 'mode'>>`
  font-size: 12px;
  font-style: italic;
  color: ${({ mode, theme }) => (mode === 'dark' ? theme.vars.newColor.white : theme.vars.newColor.darkGS3)};
`;

const StatIconWrapper = styled.div<Pick<IStatProps, 'variation'>>`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: ${({ variation }) => (variation === 'inline' ? '0 15px 0 0' : '0 0 15px')};
`;

const Placeholder = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const PlaceholderWrapper = styled.div`
  height: 100px;
  width: 90px;
`;

const StatPlaceHolder: FC = () => (
  <Placeholder>
    <PlaceholderWrapper>
      <ContentPlaceholder viewBox="0 0 100 100" preserveAspectRatio="none" responsive={true}>
        <rect x="10%" y="20%" rx="0" ry="0" width="80%" height="15%" />
        <rect x="10%" y="45%" rx="0" ry="0" width="60%" height="45%" />
      </ContentPlaceholder>
    </PlaceholderWrapper>
  </Placeholder>
);

const StatPanelPlaceholderWrapper = styled.div`
  height: 128px;
  width: 128px;
`;

const StatPanelPlaceHolder: FC = () => (
  <Placeholder>
    <StatPanelPlaceholderWrapper>
      <ContentPlaceholder viewBox="0 0 100 100" preserveAspectRatio="none" responsive={true}>
        <circle cx="50%" cy="10%" r="10%" />
        <rect x="15%" y="37.5%" rx="0" ry="0" width="70%" height="15%" />
        <rect x="25%" y="70%" rx="0" ry="0" width="50%" height="30%" />
      </ContentPlaceholder>
    </StatPanelPlaceholderWrapper>
  </Placeholder>
);

export type ValueSize = 'h1' | 'h2';
export type StatVariation = 'standard' | 'panel' | 'inline';
export type StatMode = 'light' | 'dark';

export interface IStatProps {
  /** Name of the stat. */
  name?: string;
  /** Prefix of the value. */
  prefix?: string;
  /** Value of the stat. */
  value: string | number;
  /** Optional comparison value. */
  comparisonValue?: string;
  /** Suffix of the value. */
  suffix?: string;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string | string[];
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** Optional tooltip position. Defaults to right. */
  tooltipPosition?: Position;
  /** optional icon image */
  icon?: string;
  /** optional icon colour */
  iconColour?: string;
  /** optional icon background colour */
  iconBgColour?: string;
  /** Optional unformatted raw numerical value of `value`*/
  rawValue?: number;
  /** Optional unformatted raw numerical value of `comparisonValue` */
  rawComparisonValue?: number;
  /** Show comparison difference */
  showComparisonDifference?: boolean;
  /** Show placeholder value */
  showPlaceholder?: boolean;
  /** Additional descriptive text. */
  healthText?: string;
  /** Stat variation. (Default: standard) */
  variation?: StatVariation;
  /** Stat mode. (Default: light) */
  mode?: StatMode;
  /** Position the title to the top or bottom of the value. (Default: top) */
  titlePosition?: 'top' | 'bottom';
}

export const Stat: FC<IStatProps> = ({
  comparisonValue,
  healthText,
  icon,
  iconBgColour,
  iconColour,
  mode = 'light',
  name,
  prefix,
  rawComparisonValue,
  rawValue,
  showComparisonDifference,
  showPlaceholder,
  suffix,
  titlePosition = 'top',
  tooltipDescription,
  tooltipHeading,
  tooltipPosition,
  tooltipWidth,
  value,
  variation = 'standard',
}) => {
  const theme = useTheme();

  if (showPlaceholder) {
    if (variation === 'panel') {
      return <StatPanelPlaceHolder />;
    }

    return <StatPlaceHolder />;
  }

  const renderTooltipHtml = () => {
    if (tooltipHeading) {
      return <LabelTooltip heading={tooltipHeading} description={tooltipDescription} width={tooltipWidth} />;
    } else {
      return tooltipDescription;
    }
  };

  const renderHealthMetric = () => {
    if (showComparisonDifference && typeof rawValue !== 'undefined' && typeof rawComparisonValue !== 'undefined') {
      return <HealthMetric newValue={rawValue} oldValue={rawComparisonValue} text={healthText} />;
    }

    return null;
  };

  const renderTitle = () => (
    <StatTitle variation={variation} titlePosition={titlePosition}>
      <Paragraph
        fontSize={variation === 'standard' ? null : '18px'}
        bold={variation === 'standard' ? false : true}
        color={mode === 'dark' ? theme.vars.newColor.white : null}
        htmlTag="div"
      >
        {name}

        {tooltipDescription && (
          <StatTooltip>
            <Tooltip html={renderTooltipHtml()} preventOverflow={false} position={tooltipPosition}>
              <Icon
                icon="question-circle"
                size="small"
                color={mode === 'dark' ? theme.vars.newColor.white : theme.vars.newColor.darkGS2}
              />
            </Tooltip>
          </StatTooltip>
        )}
      </Paragraph>
    </StatTitle>
  );

  const renderIcon = () => {
    if (icon) {
      return (
        <StatIconWrapper variation={variation}>
          {mode === 'dark' ? (
            <Icon icon={icon} size="medium" color={theme.vars.newColor.white} />
          ) : (
            <Avatar icon={icon} size="extra-large" color={iconColour} bg={iconBgColour} />
          )}
        </StatIconWrapper>
      );
    }
    return null;
  };

  return (
    <StatElement data-testid="Stat">
      <StatWrapper variation={variation}>
        {variation === 'panel' && renderIcon()}

        {titlePosition === 'top' && renderTitle()}

        <StatValueWrapper variation={variation} titlePosition={titlePosition}>
          {variation === 'inline' && renderIcon()}

          <StatValue variation={variation}>
            <Paragraph
              fontSize={variation === 'inline' || variation === 'panel' ? '35px' : '24px'}
              bold={true}
              color={mode === 'dark' ? theme.vars.newColor.white : null}
            >
              {prefix}
              {value}
              {suffix}
            </Paragraph>

            {!showComparisonDifference && comparisonValue && (
              <StatComparison mode={mode}>{comparisonValue}</StatComparison>
            )}
          </StatValue>
        </StatValueWrapper>

        {titlePosition === 'bottom' && renderTitle()}

        {mode === 'light' && renderHealthMetric()}
      </StatWrapper>
    </StatElement>
  );
};

export default Stat;
